import * as React from 'react';
import { useEffect } from 'react';

import { Box } from '@opendoor/bricks-next';
import { LocalizationProvider } from '@opendoor/cinderblocks/growth/auth/LocalizationContext';
import AccountForm from '@opendoor/cinderblocks/platform/auth/AccountForm';
import { analytics, globalObservability } from '@opendoor/observability/slim';
import Cookies from 'js-cookie';

import { useLocale } from 'helpers/useLocale';

import * as api from '../api';
import { REDIRECT_URLS_REGEX } from '../globals';
import LogInConnector, { AccountProps } from '../shared/AccountConnector';

const Sentry = globalObservability.getSentryClient();

const UNAUTHORIZED_STATUS_CODE = 401;

interface IAccountFormPageProps extends AccountProps {
  config?: 'resaleClosingDashboard';
  redirectTo?: string | undefined;
  emailPrefill?: string | undefined;
  updatedPassword?: boolean;
}

const AccountFormPage: React.FC<IAccountFormPageProps> = ({
  logIn,
  register,
  requestHumanStatus,
  hasGeneratedPassword,
  isNewHuman = false,
  loginRequestStatus,
  humanStatusRequestStatus,
  registrationRequestStatus,
  config,
  email,
  emailPrefill,
  requestResetPassword,
  redirectTo,
  maybeFetchSessionInfo,
  loggedIn,
  serverLoginRedirect,
  updatedPassword,
}) => {
  const locale = useLocale();

  const getSignInText = (config?: string) => {
    let loginHeaderText: string | undefined;
    let registrationHeaderText: string | undefined;
    switch (config) {
      case 'resaleClosingDashboard':
        loginHeaderText = 'Sign in to get back to your dashboard';
        registrationHeaderText = 'Sign up to get access to your dashboard';
        break;

      default:
        break;
    }

    return { loginHeaderText, registrationHeaderText };
  };

  const { loginHeaderText, registrationHeaderText } = getSignInText(config);

  useEffect(() => {
    analytics.register({ login: Cookies.get('login-test') || 'no-cookie' });
    analytics.trackEvent('login', 'load', undefined, { email, config, redirectTo, emailPrefill });
    maybeFetchSessionInfo();

    redirectIfLoggedIn();
  }, []);

  useEffect(() => {
    redirectIfLoggedIn();

    if (
      loginRequestStatus &&
      loginRequestStatus.error &&
      loginRequestStatus.statusCode !== UNAUTHORIZED_STATUS_CODE
    ) {
      Sentry.captureException?.(loginRequestStatus.error);
    }
  }, [loggedIn, loginRequestStatus]);

  const trackLoginEvent = (
    topic: 'google-auth' | 'one-time-password',
    label: string,
    extra: Record<string, unknown>,
  ) => {
    analytics.trackEvent('login', topic, label, extra);
  };

  const redirectIfLoggedIn = () => {
    if (loggedIn) {
      analytics.trackEvent('login', 'redirect', undefined, {
        email,
        config,
        redirectTo,
        serverLoginRedirect,
      });

      let redirectUrl;
      // redirectTo="/" means "came from homepage" - not a real destination preference
      const hasClientRedirect = redirectTo && redirectTo !== '/';
      const hasServerRedirect = serverLoginRedirect && serverLoginRedirect !== '/';
      if (!hasClientRedirect && hasServerRedirect) {
        redirectUrl = serverLoginRedirect;
      } else if (hasClientRedirect) {
        // allow redirection to relative urls & opendoor.com absolute urls
        if (REDIRECT_URLS_REGEX.test(redirectTo)) {
          // absolute path
          redirectUrl = redirectTo;
        } else {
          // if redirectTo does not match regex it is a relative path
          // add `/` to enforce relative only if it does not already have one
          redirectUrl = `${redirectTo[0] === '/' ? '' : '/'}${redirectTo}`;
        }
      }
      window.location.assign(redirectUrl || '/');
    }
  };

  const handlePasswordlessLoginRequest = async (email: string) => {
    trackLoginEvent('one-time-password', 'request', { email, redirectTo });
    const response = await api.requestNewTokenByEmail(email, redirectTo);
    if (!response || !response.data || response.errors) {
      const errors = response && response.errors;
      trackLoginEvent('one-time-password', 'error', { email, errors });
      return { success: false, errors };
    }

    trackLoginEvent('one-time-password', 'success', { email });
    return { success: true };
  };

  let headerText = loginHeaderText;

  if (updatedPassword) {
    headerText = 'Your password was successfully changed. Please sign in.';
  }
  const onRegister = (
    email?: string,
    name?: string,
    password?: string,
    phone?: string,
    googleIDToken?: string,
  ) => {
    register(email, name, password, phone, googleIDToken);
    //   Do not remove - GTM event for account creation
    analytics.trackEvent('session', 'register', 'account-creation', { email });
  };

  return (
    <Box
      py="$24x"
      px="$12x"
      mx="auto"
      justifyContent="center"
      alignItems="center"
      $largerThanMD={{ py: '$36x', px: 0 }}
    >
      <LocalizationProvider locale={locale}>
        <AccountForm
          analyticsName="cosmos-account-form"
          loginHeaderText={headerText}
          registrationHeaderText={registrationHeaderText}
          email={email || emailPrefill}
          hasGeneratedPassword={hasGeneratedPassword}
          error={
            loginRequestStatus.error ||
            humanStatusRequestStatus.error ||
            registrationRequestStatus.error
          }
          passwordLoading={
            !!serverLoginRedirect ||
            loginRequestStatus.isLoading ||
            registrationRequestStatus.isLoading
          }
          emailLoading={humanStatusRequestStatus.isLoading}
          isNewHuman={isNewHuman}
          onLogin={logIn}
          overrideHasSubmittedEmailCheckForLogin={!!emailPrefill}
          onRegister={onRegister}
          onHumanStatusRequest={requestHumanStatus}
          showGoogleAuth
          requestResetPassword={requestResetPassword}
          onPasswordlessLoginRequest={(email: string) => handlePasswordlessLoginRequest(email)}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default LogInConnector(AccountFormPage);
